import { Link, Outlet } from '@tanstack/react-router'
import { useState } from 'react'
import type { Session } from '@/entities/session'
import { useLogout } from '@/features/logout'
import { Avatar } from '@/shared/ui/avatar'
import { BrandLogo, EcosystemsLogo } from '@/shared/ui/brand'
import { Icon } from '@/shared/ui/icon'
import { Menu, MenuItem } from '@/shared/ui/menu'
import { ToastViewport } from '@/shared/ui/toast'
import { ZoneBadge } from '@/shared/ui/zone-badge'
import { ProductSwitcher } from './product-switcher'
import { NavBurger, NavDrawer, type NavItem, ShellSidebar } from './shell-nav'

// Витрина входит проектами: клиент думает «мой проект», а не «мои задачи».
// Плоский список остаётся вторым пунктом — им ищут задачу, когда доска и этап
// неизвестны.
const CUSTOMER_NAV = [
  { to: '/my/projects', icon: 'kanban', label: 'Мои проекты' },
  { to: '/my/tasks', icon: 'my-tasks', label: 'Мои задачи' },
] as const satisfies readonly NavItem[]

// Каркас витрины заказчика (00-architecture § 6): тот же сайдбар и drawer, что у
// команды, — разделов меньше, но раскладка приложения одна. Ни поиска (осознанная
// не-цель фазы), ни инбокса, ни таймер-бара: это работа команды.
export function CustomerShell({ session }: { session: Session }) {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const logout = useLogout()

  return (
    <>
      <div className="flex h-dvh flex-col overflow-hidden bg-bg-base text-text-primary">
        <header className="flex h-14 shrink-0 items-center border-b border-border-default">
          {/* Бренд-зона = ширина сайдбара, её правая граница продолжает вертикаль
              сайдбара: слева от линии — экосистема, справа — текущий продукт. */}
          <div className="flex h-full shrink-0 items-center gap-3 px-4 sm:px-6 md:w-60 md:border-r md:border-border-default">
            <NavBurger onOpen={() => setDrawerOpen(true)} />
            <Link
              to="/my/projects"
              aria-label="STIT Task — на главную"
              className="flex min-w-0 items-center rounded-control opacity-90 transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-border-accent focus-visible:outline-none"
            >
              {/* На узком экране — только знак: полный логотип с дескриптором
                  съедает место у свитчера и правой группы. */}
              <BrandLogo className="h-4 sm:hidden" />
              <EcosystemsLogo className="hidden h-5 sm:block" />
            </Link>
          </div>

          {/* Свитчер — тот же, что у команды, но в срезе аккаунта: только его продукты. */}
          <div className="flex h-full min-w-0 items-center px-2 sm:px-4">
            <ProductSwitcher current="task" wordmark="TASK" customerProducts={session.products} />
          </div>

          <div className="ml-auto flex h-full shrink-0 items-center gap-1 px-4 sm:px-6">
            {/* Заказчик читает те же сроки, что и команда, — зона подписана и здесь. */}
            <ZoneBadge />
            <Menu
              align="end"
              ariaLabel={`Аккаунт: ${session.name}`}
              trigger={
                <span className="flex items-center gap-2">
                  <Avatar name={session.name} size="sm" />
                  <span className="hidden max-w-40 truncate text-sm sm:block">{session.name}</span>
                  <Icon name="chevron-down" size={16} className="text-text-tertiary" />
                </span>
              }
            >
              {(close) => (
                <MenuItem
                  onSelect={() => {
                    close()
                    logout.mutate()
                  }}
                >
                  <span className="flex items-center gap-2">
                    <Icon name="logout" size={16} />
                    Выйти
                  </span>
                </MenuItem>
              )}
            </Menu>
          </div>
        </header>

        <div className="flex min-h-0 flex-1">
          <ShellSidebar items={CUSTOMER_NAV} />

          <main className="min-w-0 flex-1 overflow-y-auto pb-safe [scrollbar-gutter:stable]">
            <Outlet />
          </main>
        </div>
      </div>

      <NavDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        home="/my/projects"
        items={CUSTOMER_NAV}
      />

      <ToastViewport />
    </>
  )
}
